import React, { useState } from 'react';
import { SlidersHorizontal, Check, X } from 'lucide-react';
import DialogShell from '../ui/DialogShell';

// Feed settings (BAR-298) — a modal to pick which sources / types / categories
// show in "Latest from the regulators". State is "hidden" sets (default = show
// all), so a newly added regulator or category appears by default. The button +
// modal are self-contained; the parent owns the hidden sets and the feed.
interface FeedFilterProps {
    sources: string[];
    types: string[];
    categories: string[];
    hiddenSources: Set<string>;
    hiddenTypes: Set<string>;
    hiddenCategories: Set<string>;
    onToggleSource: (s: string) => void;
    onToggleType: (t: string) => void;
    onToggleCategory: (c: string) => void;
    onReset: () => void;
}

export default function FeedFilter({
    sources,
    types,
    categories,
    hiddenSources,
    hiddenTypes,
    hiddenCategories,
    onToggleSource,
    onToggleType,
    onToggleCategory,
    onReset,
}: FeedFilterProps) {
    const [open, setOpen] = useState(false);

    const hiddenCount = hiddenSources.size + hiddenTypes.size + hiddenCategories.size;

    const Checkmark = ({ checked }: { checked: boolean }) => (
        <span
            className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border ${
                checked ? 'bg-primary border-primary' : 'border-border-strong bg-surface'
            }`}
        >
            {checked && <Check className="h-3 w-3 text-surface" strokeWidth={3} />}
        </span>
    );

    const Column = ({
        title,
        values,
        hidden,
        onToggle,
        empty,
    }: {
        title: string;
        values: string[];
        hidden: Set<string>;
        onToggle: (v: string) => void;
        empty: string;
    }) => (
        <div className="min-w-0 flex-1">
            <div className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-fg-tertiary">
                {title}
            </div>
            {values.length === 0 ? (
                <div className="font-body text-[12px] text-fg-tertiary italic">{empty}</div>
            ) : (
                <div className="flex flex-col gap-0.5">
                    {values.map((v) => (
                        <button
                            key={v}
                            type="button"
                            onClick={() => onToggle(v)}
                            className="flex w-full items-start gap-2.5 rounded-md px-2 py-1.5 text-left hover:bg-surface-sunken transition-colors"
                        >
                            <span className="mt-0.5"><Checkmark checked={!hidden.has(v)} /></span>
                            <span className="font-body text-[13px] leading-snug text-fg">{v}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                className="inline-flex items-center gap-1.5 font-mono text-[11px] font-semibold text-secondary hover:text-primary transition-colors"
            >
                <SlidersHorizontal className="h-3.5 w-3.5" strokeWidth={1.75} />
                <span>Filter{hiddenCount > 0 ? ` (${hiddenCount})` : ''}</span>
            </button>

            {open && (
              <DialogShell
                open={open}
                onClose={() => setOpen(false)}
                ariaLabel="Feed settings"
                backdropPolicy="dismiss"
                overlayClassName="flex items-start justify-center px-4 pt-[12vh]"
                panelClassName="w-full max-w-[720px] border border-border-default overflow-hidden"
            >
                        <div className="flex items-center justify-between border-b border-divider px-5 py-3.5">
                            <h2 className="font-heading text-[20px] font-normal tracking-[-0.01em] text-fg">
                                Feed settings
                            </h2>
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                className="rounded-md p-1 text-fg-tertiary hover:bg-surface-sunken hover:text-fg transition-colors"
                                aria-label="Close"
                            >
                                <X className="h-4 w-4" strokeWidth={2} />
                            </button>
                        </div>

                        <div className="flex gap-6 px-5 py-4">
                            <Column title="Sources" values={sources} hidden={hiddenSources} onToggle={onToggleSource} empty="—" />
                            <Column title="Types" values={types} hidden={hiddenTypes} onToggle={onToggleType} empty="—" />
                            {/* Categories column only shows when categorisation is on (the
                                backend returns no category facet while it's parked). */}
                            {categories.length > 0 && (
                                <Column
                                    title="Categories"
                                    values={categories}
                                    hidden={hiddenCategories}
                                    onToggle={onToggleCategory}
                                    empty="Tagged as items are categorised"
                                />
                            )}
                        </div>

                        {/* Auto mode — not built yet (needs the institutional profile). */}
                        <div
                            className="flex items-center gap-2.5 border-t border-divider px-5 py-3 opacity-50"
                            title="Tailored to your institution's regulatory profile — coming soon"
                        >
                            <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-border-strong bg-surface" />
                            <span className="font-body text-[13px] text-fg">
                                Auto — tailored to your institution{' '}
                                <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-fg-tertiary">soon</span>
                            </span>
                        </div>

                        <div className="flex items-center justify-between border-t border-divider px-5 py-3">
                            <button
                                type="button"
                                onClick={onReset}
                                disabled={hiddenCount === 0}
                                className="font-mono text-[11px] text-fg-secondary hover:text-primary transition-colors disabled:opacity-40 disabled:cursor-default"
                            >
                                Reset
                            </button>
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                className="rounded-md bg-primary px-4 py-1.5 font-body text-[13px] font-medium text-surface hover:bg-primary-hover transition-colors"
                            >
                                Done
                            </button>
                        </div>
              </DialogShell>
            )}
        </>
    );
}
